import { useState, useRef, useContext, useMemo } from 'react'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import Input from '@mui/material/Input';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import { Controller, ControllerRenderProps, FieldValues, UseControllerProps } from 'react-hook-form';
import { AppContext } from '../../context';
import util, { byteConverter } from '../../helpers/util.helper';
import { Typography } from '@mui/material';

interface InputFileProps {
    label: string,
    icon: IconProp,
    fileTypes?: string,
    controller: UseControllerProps,
    helperText?: string,
    multiple?: boolean,
    minSizeMb?: number,
    maxSizeMb?: number
    errors: any
}
const MB = 1024 * 1024;

/**
 * No se genera con el metodo 'register' de useForm por problemas para
 * retirar una o varias imagenes cargadas
 * @param param0 
 * @returns 
 */
export default function InputFileImg({ label, icon, fileTypes = '*', controller, errors, helperText, multiple, minSizeMb = 0.25, maxSizeMb = 10 }: InputFileProps) {
    const appContext = useContext(AppContext)
    const hiddenFileInput = useRef<HTMLInputElement | null>(null);
    const [fileName, setFileName] = useState("");

    const handleClick = () => {
        hiddenFileInput.current?.click();
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>, field: ControllerRenderProps<FieldValues, string>) => {
        if (!event.target.files) {
            return
        }
        if (multiple) {
            let files = [...event.target.files];
            let removePrevious = (field.value || []).filter((f: File) => !files.find(fi => fi.name === f.name));
            field.onChange({ target: { value: [...files, ...removePrevious] } })
        } else {
            event.target.files[0]?.name && setFileName(event.target.files[0].name);
            field.onChange({ target: { value: [...event.target.files] } })
        }
    }

    const filterByMinSize = (file: File) => {
        return file?.size > (MB * minSizeMb); //Bytes
    };

    const filterByMaxSize = (file: File) => {
        return file?.size <= (MB * maxSizeMb); //Bytes
    };

    const filterByType = (file: File) => {
        return file?.type === "image/jpeg";
    };

    function handleDeleteItem(file: File, field: ControllerRenderProps<FieldValues, string>) {
        return () => {
            if (field.value) {
                let filter = [...field.value].filter(f => f.name !== file.name)
                field.onChange({ target: { value: filter } })
            }
        }
    }

    function getErrorsByName() {
        let nameParts = controller.name.split(".")
        let error = { ...errors }
        for (let part of nameParts) {
            if (error) {
                error = error[part]
            }
        }
        return error
    }

    return (
        <Controller
            {...controller}
            rules={{
                ...controller.rules,
                validate: {
                    minsize: (v: any) => filterByMinSize(v?.[0]) || `El archivo no cumple con el tamaño minimo recomendado (${byteConverter(MB * minSizeMb, 0)}).`,
                    maxsize: (v: any) => filterByMaxSize(v?.[0]) || `El archivo no cumple con el tamaño máximo permitido (${maxSizeMb}mb).`,
                    type: (v: any) => filterByType(v?.[0]) || "El formato de la imágen debe ser .jpg.",
                }
            }}
            render={({ field }) => (
                <FormControl variant="standard" fullWidth>
                    <InputLabel
                        htmlFor="input-with-icon-adornment"
                        onClick={handleClick}
                        error={getErrorsByName() !== undefined}
                        disabled={appContext?.loading}
                    >
                        {label}
                    </InputLabel>
                    <Input
                        id="input-with-icon-adornment"
                        startAdornment={
                            <InputAdornment position="start">
                                <FontAwesomeIcon icon={icon} />
                            </InputAdornment>
                        }
                        onClick={handleClick}
                        value={fileName}
                        placeholder="Seleccione un archivo..."
                        error={getErrorsByName() !== undefined}
                        onKeyDown={(e) => e.preventDefault}
                        onCopy={(e) => e.preventDefault}
                        onPaste={(e) => e.preventDefault}
                        onContextMenu={(e) => e.preventDefault}
                        disabled={appContext?.loading}
                    />
                    <input
                        type="file"
                        multiple={!!multiple}
                        accept={fileTypes}
                        style={{ display: "none" }}
                        ref={(e) => {
                            hiddenFileInput.current = e
                        }}
                        onChange={e => {
                            handleChange(e, field)
                        }}
                    />
                    {getErrorsByName() && <FormHelperText error>{getErrorsByName()?.message}</FormHelperText>}
                    {helperText && <FormHelperText>{helperText}</FormHelperText>}
                    {field.value && <div className='form-contain-preview'>
                        {[...field.value].map(f => <ItemPreview loading={appContext?.loading} file={f} key={f.name} onDelete={multiple ? handleDeleteItem(f, field) : undefined} />)}
                    </div>}
                </FormControl>)}
        />
    )
}

interface ItemPreviewProps {
    file: File,
    onDelete?: VoidFunction,
    loading?: boolean
}

function ItemPreview({ file, onDelete, loading }: ItemPreviewProps) {
    const Preview = useMemo(() => {
        let detail = util.getFileExtension(file.name)
        if (detail.type === 'image') {
            let url = URL.createObjectURL(file)
            return <img src={url} alt={file.name} />
        }
        return <div className='form-contain-preview-item-icon'><FontAwesomeIcon className='m-auto' icon={'file'} /></div>
    }, [file])

    return <div className='form-contain-preview-item'>
        {onDelete && <div className='form-contain-preview-item-header'>
            <button onClick={onDelete} disabled={loading}>
                <FontAwesomeIcon icon={'times'} />
            </button>
        </div>}
        {Preview}
        <Typography className='form-contain-preview-item-footer' variant='caption'>{file.name}</Typography>
    </div>
}